// clipSlice.js - Redux Toolkit slice for clip management
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { clipAPI } from "./api";
import { logout } from "../auth/index";
import { createMiddleware } from "../middlewareUtil";

// Initial state
const initialState = {
  creating: false,
  loading: false,
  loaded: false,
  saving: false,
  clip: {},
};

export const deleteClip = createAsyncThunk(
  "clip/deleteClip",
  async (clip, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    const result = await clipAPI.deleteClip(accessToken, clip);

    if (result.success) {
      return result.data;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to delete clip");
  }
);

export const loadClip = createAsyncThunk(
  "clip/loadClip",
  async ({ caseId, clipId }, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    const result = await clipAPI.loadClip(accessToken, caseId, clipId);

    if (result.success) {
      return result.data;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to load clip");
  }
);

export const restoreClip = createAsyncThunk(
  "clip/restoreClip",
  async (clip, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    const result = await clipAPI.restoreClip(accessToken, clip);

    if (result.success) {
      return result.data;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to restore clip");
  }
);

export const revertClip = createAsyncThunk(
  "clip/revertClip",
  async (
    { caseId, clipId, commitId },
    { getState, dispatch, rejectWithValue }
  ) => {
    const accessToken = getState().auth.accessCode;

    const result = await clipAPI.revertClip(
      accessToken,
      caseId,
      clipId,
      commitId
    );

    if (result.success) {
      return result.data;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to revert clip");
  }
);

export const trashClip = createAsyncThunk(
  "clip/trashClip",
  async (clip, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    const result = await clipAPI.trashClip(accessToken, clip);

    if (result.success) {
      return result.data;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to trash clip");
  }
);

export const updateClipMetadata = createAsyncThunk(
  "clip/updateClipMetadata",
  async (cur, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    const result = await clipAPI.updateClipMetadata(accessToken, cur);

    if (result.success) {
      return result.data;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to update clip metadata");
  }
);

// Create the slice
export const clipSlice = createSlice({
  name: "clip",
  initialState,
  reducers: {
    reset: (_state, _action) => {
      return cloneDeep(initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      // Delete clip - resets state after successful deletion
      .addCase(deleteClip.fulfilled, (_state, _action) => {
        return cloneDeep(initialState);
      })

      // Reset state even on error as per original implementation
      .addCase(deleteClip.rejected, (_state, _action) => {
        return cloneDeep(initialState);
      })

      // Load clip
      .addCase(loadClip.pending, (state, _action) => {
        state.loading = true;
      })
      .addCase(loadClip.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.clip = action.payload;
      })
      .addCase(loadClip.rejected, (state, _action) => {
        state.loading = false;
        state.loaded = true;
        state.clip = {};
      })

      // Restore clip - resets state after successful restore
      .addCase(restoreClip.fulfilled, (_state, _action) => {
        return cloneDeep(initialState);
      })

      // Reset state even on error as per original implementation
      .addCase(restoreClip.rejected, (_state, _action) => {
        return cloneDeep(initialState);
      })

      // Revert clip
      .addCase(revertClip.pending, (state, _action) => {
        state.loading = true;
      })
      .addCase(revertClip.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.clip = action.payload;
      })
      .addCase(revertClip.rejected, (state, _action) => {
        state.loading = false;
        state.loaded = true;
        state.clip = {};
      })

      // Trash clip - resets state after successful trashing
      .addCase(trashClip.fulfilled, (_state, _action) => {
        return cloneDeep(initialState);
      })

      // Reset state even on error as per original implementation
      .addCase(trashClip.rejected, (_state, _action) => {
        return cloneDeep(initialState);
      })

      // Update clip metadata
      .addCase(updateClipMetadata.pending, (state, _action) => {
        state.saving = true;
        state.loading = true;
      })
      .addCase(updateClipMetadata.fulfilled, (state, action) => {
        state.saving = false;
        state.loaded = true;
        state.loading = false;
        state.clip = action.payload;
      })
      .addCase(updateClipMetadata.rejected, (state, _action) => {
        state.saving = false;
        state.loaded = true;
        state.loading = false;
        state.clip = {};
      });
  },
});

export const clipMiddleware = createMiddleware([
  "clip/loadClip/pending",
  "clip/updateClipMetadata/pending",
  "clip/revertClip/pending",
]);

// Export actions
export const { reset } = clipSlice.actions;

// Export the reducer
export default clipSlice.reducer;
